import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  appFetch,
  appFetchJson,
  clearStoredSession,
} from "../appFetch";

const AuthContext = createContext(null);

function readStoredJson(key) {
  const value = localStorage.getItem(key);

  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(() =>
    readStoredJson("user_data")
  );

  const [admin, setAdmin] = useState(() =>
    readStoredJson("admin_user")
  );

  const [sessionMessage, setSessionMessage] =
    useState("");

  const hasUserSession = Boolean(
    localStorage.getItem("user_access_token") &&
      localStorage.getItem("user_refresh_token") &&
      user
  );

  const hasAdminSession = Boolean(
    localStorage.getItem("admin_access_token") &&
      localStorage.getItem("admin_refresh_token") &&
      admin
  );

  const loginUser = useCallback((sessionData) => {
    localStorage.setItem(
      "user_access_token",
      sessionData.access
    );

    localStorage.setItem(
      "user_refresh_token",
      sessionData.refresh
    );

    localStorage.setItem(
      "user_data",
      JSON.stringify(sessionData.user)
    );

    if (sessionData.organization) {
      localStorage.setItem(
        "organization",
        JSON.stringify(sessionData.organization)
      );
    }

    clearStoredSession("admin");

    setAdmin(null);
    setUser(sessionData.user);
    setSessionMessage("");
  }, []);

  const loginAdmin = useCallback((sessionData) => {
    localStorage.setItem(
      "admin_access_token",
      sessionData.access
    );

    localStorage.setItem(
      "admin_refresh_token",
      sessionData.refresh
    );

    localStorage.setItem(
      "admin_user",
      JSON.stringify(sessionData.user)
    );

    clearStoredSession("user");

    setUser(null);
    setAdmin(sessionData.user);
    setSessionMessage("");
  }, []);

  const logoutUser = useCallback(
    (
      message = "",
      redirect = true
    ) => {
      clearStoredSession("user");
      setUser(null);

      if (message) {
        sessionStorage.setItem(
          "auth_session_message",
          message
        );
        setSessionMessage(message);
      }

      if (redirect) {
        navigate("/login", {
          replace: true,
        });
      }
    },
    [navigate]
  );

  const logoutAdmin = useCallback(
    (
      message = "",
      redirect = true
    ) => {
      clearStoredSession("admin");
      setAdmin(null);

      if (message) {
        sessionStorage.setItem(
          "auth_session_message",
          message
        );
        setSessionMessage(message);
      }

      if (redirect) {
        navigate("/admin-panel/login", {
          replace: true,
        });
      }
    },
    [navigate]
  );

  const logout = useCallback(
    (sessionType, message = "") => {
      if (sessionType === "admin") {
        logoutAdmin(message);
        return;
      }

      logoutUser(message);
    },
    [
      logoutAdmin,
      logoutUser,
    ]
  );

  const clearSessionMessage = useCallback(() => {
    sessionStorage.removeItem(
      "auth_session_message"
    );
    setSessionMessage("");
  }, []);

  const fetchUser = useCallback(
    (url, options = {}) =>
      appFetch(url, options, "user"),
    []
  );

  const fetchAdmin = useCallback(
    (url, options = {}) =>
      appFetch(url, options, "admin"),
    []
  );

  const fetchUserJson = useCallback(
    (url, options = {}) =>
      appFetchJson(url, options, "user"),
    []
  );

  const fetchAdminJson = useCallback(
    (url, options = {}) =>
      appFetchJson(url, options, "admin"),
    []
  );

  useEffect(() => {
    const storedMessage = sessionStorage.getItem(
      "auth_session_message"
    );

    if (storedMessage) {
      setSessionMessage(storedMessage);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleSessionExpired = (event) => {
      const sessionType =
        event.detail?.sessionType || "user";

      const message =
        event.detail?.message ||
        "Tu sesión ha expirado. Inicia sesión nuevamente.";

      sessionStorage.setItem(
        "auth_session_message",
        message
      );

      setSessionMessage(message);

      if (sessionType === "admin") {
        setAdmin(null);

        navigate("/admin-panel/login", {
          replace: true,
        });

        return;
      }

      setUser(null);

      navigate("/login", {
        replace: true,
      });
    };

    window.addEventListener(
      "auth:session-expired",
      handleSessionExpired
    );

    return () => {
      window.removeEventListener(
        "auth:session-expired",
        handleSessionExpired
      );
    };
  }, [navigate]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (
        event.key === "user_data" ||
        event.key === "user_access_token" ||
        event.key === "user_refresh_token"
      ) {
        setUser(readStoredJson("user_data"));
      }

      if (
        event.key === "admin_user" ||
        event.key === "admin_access_token" ||
        event.key === "admin_refresh_token"
      ) {
        setAdmin(readStoredJson("admin_user"));
      }
    };

    window.addEventListener(
      "storage",
      handleStorage
    );

    return () => {
      window.removeEventListener(
        "storage",
        handleStorage
      );
    };
  }, []);

  const value = useMemo(
    () => ({
      user,
      admin,
      hasUserSession,
      hasAdminSession,
      sessionMessage,
      loginUser,
      loginAdmin,
      logoutUser,
      logoutAdmin,
      logout,
      clearSessionMessage,
      fetchUser,
      fetchAdmin,
      fetchUserJson,
      fetchAdminJson,
    }),
    [
      user,
      admin,
      hasUserSession,
      hasAdminSession,
      sessionMessage,
      loginUser,
      loginAdmin,
      logoutUser,
      logoutAdmin,
      logout,
      clearSessionMessage,
      fetchUser,
      fetchAdmin,
      fetchUserJson,
      fetchAdminJson,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      "useAuth debe utilizarse dentro de AuthProvider."
    );
  }

  return context;
}

export default AuthContext;